import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFilterStore } from '../store/filter-store';
import { useLocationInformation } from '@/features/location/hooks/useLocationInformation';
import { Input } from '@/components/ui/Input';
import { useDebounce } from 'use-debounce';
import { FILTER_DEBOUNCE_MS } from '@/utils/constants';

/**
 * Price filter section
 */
export function PriceFilter() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const maxPricePerKwh = useFilterStore((s) => s.maxPricePerKwh);
  const setMaxPrice = useFilterStore((s) => s.setMaxPrice);

  // Read the currency from the shared location query directly (rather than a
  // prop drilled through the accordion) so the suffix updates as soon as the
  // map moves to another country — not only when the section is re-opened.
  const { data: locationInfo } = useLocationInformation();
  const currency = locationInfo?.currency;

  const [localValue, setLocalValue] = useState(
    maxPricePerKwh > 0 ? maxPricePerKwh.toString() : ''
  );
  const [debouncedValue] = useDebounce(localValue, FILTER_DEBOUNCE_MS);

  // Update store when debounced value changes
  useEffect(() => {
    const numValue = parseFloat(debouncedValue);
    if (!isNaN(numValue) && numValue >= 0) {
      setMaxPrice(numValue);
    } else if (debouncedValue === '') {
      setMaxPrice(0);
    }
  }, [debouncedValue, setMaxPrice]);

  // Clear the input when the store is reset externally (e.g. resetFilters).
  // Must not depend on localValue: while the debounce is pending the store is
  // still 0, so re-running on every keystroke would wipe what the user typed.
  useEffect(() => {
    if (maxPricePerKwh === 0) {
      setLocalValue('');
    }
  }, [maxPricePerKwh]);

  // Hide price filter when operator_scope=restrict
  const operatorScope = searchParams.get('operator_scope');
  if (operatorScope === 'restrict') {
    return null;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Decimal keyboards in e.g. da/de locales type a comma
    const value = e.target.value.replace(',', '.');
    // Allow empty string, numbers, and decimals
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setLocalValue(value);
    }
  };

  // Use currency symbol from location info if available, otherwise just show "/kWh"
  const currencySuffix = currency?.symbol ? `${currency.symbol}/kWh` : '/kWh';

  return (
    <div className="space-y-3 pt-3 pb-2">
      <Input
        type="text"
        inputMode="decimal"
        placeholder={t('filters.price.placeholder')}
        value={localValue}
        onChange={handleChange}
        suffix={currencySuffix}
      />
    </div>
  );
}
